"use client"

import * as React from "react"
import { useNavigate } from "react-router-dom"
import { Check, ChevronsUpDown, Loader2, Search, User, X, UserPlus, Mail, AlertCircle, CheckCircle } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Badge } from "@/components/ui/badge"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Skeleton } from "@/components/ui/skeleton"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { api } from "@/lib/api/client"

import type {
  UserSelectOption,
  UserSelectProps,
  UserSearchRequest,
  UserSearchResponse
} from "@/lib/types/user-select"

// Debounce hook for search input
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = React.useState<T>(value)

  React.useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])

  return debouncedValue
}

// Helper function to check if a string looks like an email
function isEmailLike(value: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(value.trim());
}

// Helper function to validate email format and provide feedback
function validateEmail(email: string): { isValid: boolean; message?: string; domain?: string } {
  const trimmed = email.trim();

  if (!trimmed) {
    return { isValid: false };
  }

  if (!isEmailLike(trimmed)) {
    return { isValid: false, message: "Please enter a valid email address" };
  }

  // Check for common issues
  const [localPart, domain] = trimmed.split('@');

  if (localPart.length === 0) {
    return { isValid: false, message: "Email address cannot start with @" };
  }

  if (domain && domain.length === 0) {
    return { isValid: false, message: "Email address cannot end with @" };
  }

  // Check for suspicious domains (optional - could be removed if not needed)
  const suspiciousDomains = ['test.com', 'example.com', 'temp.com'];
  if (domain && suspiciousDomains.includes(domain.toLowerCase())) {
    return { isValid: false, message: "Please use a real email address" };
  }

  return { isValid: true, domain };
}

export function UserSelect({
  value = [],
  onChange,
  multiple = false,
  placeholder = "Search users...",
  disabled = false,
  excludeUsers = [],
  roleFilter,
  statusFilter,
  className,
  maxHeight = 300,
  maxUsers,
  allowCreateUser = false,
  streamId,
  accessType = 'direct',
  onUserCreated,
  ...props
}: UserSelectProps) {
  const navigate = useNavigate()
  const [open, setOpen] = React.useState(false)
  const [searchQuery, setSearchQuery] = React.useState("")
  const [selectedUserIds, setSelectedUserIds] = React.useState<number[]>(
    Array.isArray(value) ? value : value ? [value] : []
  )
  const [selectedUsers, setSelectedUsers] = React.useState<UserSelectOption[]>([])
  const [availableUsers, setAvailableUsers] = React.useState<UserSelectOption[]>([])
  const [isLoading, setIsLoading] = React.useState(false)
  const [hasMore, setHasMore] = React.useState(false)
  const [page, setPage] = React.useState(0)
  const [error, setError] = React.useState<string | null>(null)
  const [isCreatingUser, setIsCreatingUser] = React.useState(false)
  const [creationSuccess, setCreationSuccess] = React.useState<UserSelectOption | null>(null)

  const debouncedSearchQuery = useDebounce(searchQuery, 300)

  // Search users API call
  const searchUsers = React.useCallback(async (
    query: string,
    currentPage: number = 0,
    append: boolean = false
  ) => {
    try {
      setIsLoading(true)
      setError(null)

      const searchParams: UserSearchRequest = {
        query: query || undefined,
        page: currentPage,
        pageSize: 20,
        excludeIds: excludeUsers.map(id => typeof id === 'number' ? id : parseInt(String(id))),
        role: roleFilter,
        status: statusFilter
      }

      const response = await api.post<UserSearchResponse>('/api/users/search', searchParams)

      if (!response.success || !response.data) {
        throw new Error(response.error?.message || 'Failed to search users')
      }

      const data = response.data

      if (append) {
        setAvailableUsers(prev => [...prev, ...data.users])
      } else {
        setAvailableUsers(data.users)
      }

      setHasMore(data.hasMore)
      setPage(data.page)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to search users')
      setAvailableUsers([])
      setHasMore(false)
    } finally {
      setIsLoading(false)
    }
  }, [excludeUsers, roleFilter, statusFilter])

  // Initial load and search
  React.useEffect(() => {
    if (open) {
      searchUsers(debouncedSearchQuery)
    }
  }, [open, debouncedSearchQuery, searchUsers])

  // Load more users
  const loadMore = React.useCallback(() => {
    searchUsers(debouncedSearchQuery, page + 1, true)
  }, [debouncedSearchQuery, page, searchUsers])

  // Handle user selection
  const handleUserSelect = React.useCallback((user: UserSelectOption) => {
    if (multiple) {
      const isSelected = selectedUserIds.includes(user.id)
      const newSelectedIds = isSelected
        ? selectedUserIds.filter(id => id !== user.id)
        : [...selectedUserIds, user.id]

      const newSelectedUsers = isSelected
        ? selectedUsers.filter(u => u.id !== user.id)
        : [...selectedUsers, user]

      setSelectedUserIds(newSelectedIds)
      setSelectedUsers(newSelectedUsers)
      onChange(newSelectedUsers)
    } else {
      const newSelectedUsers = [user]
      setSelectedUserIds([user.id])
      setSelectedUsers(newSelectedUsers)
      onChange(newSelectedUsers)
      setOpen(false)
    }
  }, [multiple, selectedUserIds, selectedUsers, onChange])

  // Remove selected user
  const removeUser = React.useCallback((userId: number) => {
    const newSelectedIds = selectedUserIds.filter(id => id !== userId)
    const newSelectedUsers = selectedUsers.filter(u => u.id !== userId)
    setSelectedUserIds(newSelectedIds)
    setSelectedUsers(newSelectedUsers)
    onChange(newSelectedUsers)
  }, [selectedUserIds, selectedUsers, onChange])

  // Clear all selected users
  const clearAll = React.useCallback(() => {
    setSelectedUserIds([])
    setSelectedUsers([])
    onChange([])
  }, [onChange])

  // Handle creating a new user
  const handleCreateUser = React.useCallback(async (email: string) => {
    if (!allowCreateUser || !isEmailLike(email)) return

    try {
      setIsCreatingUser(true)
      setError(null)

      // Call the API to create user and grant access
      const response = await api.post('/api/users/create-and-grant', {
        email: email.trim(),
        streamId,
        accessType,
        role: 'member' // Default role for created users
      })

      if (response.success && response.data && (response.data as any).user) {
        const userData = (response.data as any).user;
        const newUser: UserSelectOption = {
          id: userData.id,
          name: userData.name,
          email: userData.email,
          role: userData.role,
          status: userData.status,
        }

        // Add to selected users
        const newSelectedUsers = multiple ? [...selectedUsers, newUser] : [newUser]
        const newSelectedIds = multiple ? [...selectedUserIds, newUser.id] : [newUser.id]

        setSelectedUsers(newSelectedUsers)
        setSelectedUserIds(newSelectedIds)
        onChange(newSelectedUsers)

        // Show success state briefly
        setCreationSuccess(newUser)
        setTimeout(() => setCreationSuccess(null), 2000)

        // Call the onUserCreated callback if provided
        onUserCreated?.(newUser)

        // Clear search and close dropdown for single select
        if (!multiple) {
          setSearchQuery('')
          setOpen(false)
        }
      } else {
        setError(response.error?.message || 'Failed to create user')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create user')
    } finally {
      setIsCreatingUser(false)
    }
  }, [allowCreateUser, multiple, selectedUsers, selectedUserIds, onChange, onUserCreated, streamId, accessType])

  // Get display text for trigger button
  const getTriggerText = React.useMemo(() => {
    if (selectedUsers.length === 0) {
      return placeholder
    }

    if (multiple) {
      if (selectedUsers.length === 1) {
        return `${selectedUsers[0].name} (${selectedUsers[0].email})`
      }
      return `${selectedUsers.length} users selected`
    }

    return selectedUsers[0]?.name || placeholder
  }, [selectedUsers, placeholder, multiple])

  // Check if user is selected
  const isUserSelected = React.useCallback((userId: number) => {
    return selectedUserIds.includes(userId)
  }, [selectedUserIds])

  // Handle navigation to user profile
  const handleUserProfileClick = React.useCallback((userId: number, event: React.MouseEvent) => {
    event.stopPropagation() // Prevent triggering the selection handler
    navigate(`/admin/users/${userId}`)
  }, [navigate])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-label={multiple ? "Select multiple users" : "Select a user"}
          aria-describedby={selectedUsers.length > 0 ? "selected-users-count" : undefined}
          className={cn(
            "w-full justify-between min-h-10",
            selectedUsers.length === 0 && "text-muted-foreground",
            creationSuccess && "border-success/30 bg-success/10",
            className
          )}
          disabled={disabled}
          {...props}
        >
          <span className="truncate text-left">{getTriggerText}</span>
          <div className="flex items-center gap-1 ml-2">
            {creationSuccess && <CheckCircle className="h-4 w-4 text-success shrink-0" />}
            <ChevronsUpDown className={cn(
              "h-4 w-4 shrink-0 opacity-50",
              open && "opacity-100"
            )} />
          </div>
        </Button>
      </PopoverTrigger>

      {/* Hidden accessibility description */}
      {selectedUsers.length > 0 && (
        <div id="selected-users-count" className="sr-only">
          {multiple
            ? `${selectedUsers.length} users selected`
            : `${selectedUsers[0]?.name} selected`
          }
        </div>
      )}

      <PopoverContent
        className="w-full p-0"
        align="start"
        style={{ width: 'var(--radix-popover-trigger-width)' }}
      >
        <div className="flex flex-col">
          {/* Search Input */}
          <div className="p-2 border-b">
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={allowCreateUser ? "Search users or enter email..." : "Search users..."}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  // Allow Enter to create user if it's a valid email
                  if (e.key === 'Enter' && allowCreateUser && isEmailLike(searchQuery) && !isCreatingUser) {
                    e.preventDefault();
                    handleCreateUser(searchQuery);
                  }
                }}
                className={cn(
                  "pl-8 pr-8",
                  allowCreateUser && isEmailLike(searchQuery) && "border-primary/50 focus:border-primary"
                )}
                autoFocus
                autoComplete="off"
              />
              {searchQuery && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1 h-6 w-6 p-0 hover:bg-muted"
                  onClick={() => setSearchQuery('')}
                >
                  <X className="h-3 w-3" />
                </Button>
              )}
            </div>

            {/* Email validation feedback */}
            {allowCreateUser && searchQuery && isEmailLike(searchQuery) && (
              <div className="mt-2">
                {(() => {
                  const validation = validateEmail(searchQuery);
                  if (!validation.isValid) {
                    return (
                      <Alert variant="destructive" className="py-2">
                        <AlertCircle className="h-3 w-3" />
                        <AlertDescription className="text-xs">
                          {validation.message}
                        </AlertDescription>
                      </Alert>
                    );
                  }
                  return (
                    <Alert className="py-2 border-success/20 bg-success/10">
                      <CheckCircle className="h-3 w-3 text-success" />
                      <AlertDescription className="text-xs text-success-foreground">
                        Valid email address - ready to create user
                      </AlertDescription>
                    </Alert>
                  );
                })()}
              </div>
            )}
          </div>

          {/* Success Message */}
          {creationSuccess && (
            <div className="p-2 border-b bg-success/10">
              <Alert className="border-success/20">
                <CheckCircle className="h-4 w-4 text-success" />
                <AlertDescription className="text-sm text-success-foreground">
                  <strong>{creationSuccess.name}</strong> has been created and granted access!
                </AlertDescription>
              </Alert>
            </div>
          )}

          {/* Selected Users Summary */}
          {selectedUsers.length > 0 && multiple && (
            <div className="p-2 border-b bg-muted/50">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">
                  {selectedUsers.length} selected
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearAll}
                  className="h-auto p-1 text-xs"
                >
                  Clear all
                </Button>
              </div>
            </div>
          )}

          {/* User List */}
          <ScrollArea style={{ height: Math.min(maxHeight, 300) }}>
            {error ? (
              <div className="p-4">
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription className="text-sm">
                    <div className="font-medium mb-1">Search failed</div>
                    <div className="text-xs">{error}</div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => searchUsers(debouncedSearchQuery)}
                      className="mt-2 h-7 text-xs"
                    >
                      <Search className="mr-1 h-3 w-3" />
                      Try again
                    </Button>
                  </AlertDescription>
                </Alert>
              </div>
            ) : availableUsers.length === 0 && !isLoading ? (
              <div className="p-4">
                {debouncedSearchQuery ? (
                  <div>
                    {allowCreateUser && isEmailLike(debouncedSearchQuery) && validateEmail(debouncedSearchQuery).isValid ? (
                      <div className="space-y-4">
                        {/* User Creation Preview */}
                        <div className="rounded-lg border bg-muted/30 p-3">
                          <div className="flex items-center gap-3">
                            <Avatar className="h-10 w-10">
                              <AvatarFallback className="bg-primary/10 text-primary">
                                <UserPlus className="h-5 w-5" />
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="text-sm font-medium truncate">
                                  {debouncedSearchQuery.split('@')[0]}
                                </p>
                                <Badge variant="secondary" className="text-xs">
                                  New User
                                </Badge>
                              </div>
                              <div className="flex items-center gap-1 mt-1">
                                <Mail className="h-3 w-3 text-muted-foreground" />
                                <p className="text-xs text-muted-foreground truncate">
                                  {debouncedSearchQuery}
                                </p>
                              </div>
                              <div className="flex items-center gap-2 mt-1">
                                <Badge variant="outline" className="text-xs">
                                  Member
                                </Badge>
                                <span className="text-xs text-muted-foreground">
                                  Will be granted {accessType} access
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Create Button */}
                        <Button
                          onClick={() => handleCreateUser(debouncedSearchQuery)}
                          disabled={isCreatingUser}
                          className="w-full"
                          size="sm"
                        >
                          {isCreatingUser ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Creating user and granting access...
                            </>
                          ) : (
                            <>
                              <UserPlus className="mr-2 h-4 w-4" />
                              Create User & Grant Access
                            </>
                          )}
                        </Button>

                        <p className="text-xs text-muted-foreground text-center">
                          Press Enter or click the button above
                        </p>
                      </div>
                    ) : (
                      <div className="text-center text-sm text-muted-foreground">
                        <User className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p>No users found</p>
                        <p className="text-xs">
                          {allowCreateUser
                            ? "Try a different search term or enter a valid email address"
                            : "Try a different search term"
                          }
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center text-sm text-muted-foreground">
                    <Search className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>Start typing to search users</p>
                    {allowCreateUser && (
                      <div className="mt-2 p-2 bg-muted/50 rounded-md">
                        <p className="text-xs font-medium mb-1">💡 Tip: Create new users</p>
                        <p className="text-xs">
                          Enter any email address to create a new user account and grant them access
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="p-1">
                {availableUsers.map((user) => (
                  <div
                    key={user.id}
                    className={cn(
                      "flex items-center space-x-2 p-3 rounded-md cursor-pointer transition-colors",
                      "hover:bg-accent focus:bg-accent focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1",
                      isUserSelected(user.id) && "bg-accent border border-primary/20"
                    )}
                    onClick={() => handleUserSelect(user)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleUserSelect(user);
                      }
                    }}
                    role="option"
                    aria-selected={isUserSelected(user.id)}
                    tabIndex={0}
                    aria-label={`Select ${user.name} (${user.email})`}
                  >
                    {multiple && (
                      <Checkbox
                        checked={isUserSelected(user.id)}
                        onChange={() => {}} // Handled by parent onClick
                        className="flex-shrink-0"
                        aria-label={`Select ${user.name}`}
                      />
                    )}
                    <Avatar className="h-8 w-8 flex-shrink-0">
                      <AvatarImage src={user.avatar} alt={`${user.name} avatar`} />
                      <AvatarFallback>
                        {user.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          className="font-medium truncate text-left hover:underline focus:outline-none focus:underline cursor-pointer"
                          onClick={(e) => handleUserProfileClick(user.id, e)}
                          title={`View ${user.name}'s profile`}
                        >
                          {user.name}
                        </button>
                        {user.role && (
                          <Badge
                            variant={user.role === 'admin' ? 'default' : user.role === 'media_team' ? 'secondary' : 'outline'}
                            className="text-xs"
                          >
                            {user.role.replace('_', ' ')}
                          </Badge>
                        )}
                        {user.status === 'pending' && (
                          <Badge variant="outline" className="text-xs text-warning-foreground">
                            Pending
                          </Badge>
                        )}
                        {user.status === 'banned' && (
                          <Badge variant="destructive" className="text-xs">
                            Banned
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground truncate mt-0.5">
                        {user.email}
                      </p>
                      {user.lastActive && (
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Last active: {new Date(user.lastActive).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                    {!multiple && isUserSelected(user.id) && (
                      <Check className="h-4 w-4 flex-shrink-0 text-primary" aria-hidden="true" />
                    )}
                  </div>
                ))}

                {/* Loading State */}
                {isLoading && (
                  <div className="p-2 space-y-3">
                    {Array.from({ length: 3 }).map((_, i) => (
                      <div key={i} className="flex items-center space-x-2 p-2 rounded-md">
                        <Skeleton className="h-8 w-8 rounded-full flex-shrink-0" />
                        <div className="flex-1 space-y-2">
                          <div className="flex items-center gap-2">
                            <Skeleton className="h-4 w-24" />
                            <Skeleton className="h-4 w-16" />
                          </div>
                          <Skeleton className="h-3 w-32" />
                          <Skeleton className="h-3 w-20" />
                        </div>
                        <Skeleton className="h-4 w-4 flex-shrink-0" />
                      </div>
                    ))}
                    <div className="flex justify-center py-2">
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Loader2 className="h-3 w-3 animate-spin" />
                        Searching users...
                      </div>
                    </div>
                  </div>
                )}

                {/* Load More Button */}
                {hasMore && !isLoading && (
                  <div className="p-2 border-t">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={loadMore}
                      className="w-full"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Loading...
                        </>
                      ) : (
                        'Load more users'
                      )}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </ScrollArea>
        </div>
      </PopoverContent>

      {/* Selected Users Display (when multiple and collapsed) */}
      {multiple && selectedUsers.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {selectedUsers.slice(0, 3).map((user) => (
            <Badge
              key={user.id}
              variant="secondary"
              className="text-xs"
            >
              {user.name}
              <Button
                variant="ghost"
                size="sm"
                className="ml-1 h-auto p-0 text-xs hover:bg-transparent"
                onClick={(e) => {
                  e.stopPropagation()
                  removeUser(user.id)
                }}
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          ))}
          {selectedUsers.length > 3 && (
            <Badge variant="secondary" className="text-xs">
              +{selectedUsers.length - 3} more
            </Badge>
          )}
        </div>
      )}
    </Popover>
  )
}